import { HttpLink, ApolloLink, concat, InMemoryCache, ApolloClient } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { onError } from "@apollo/client/link/error";

const handleTokenExpiry = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("role");
  window.location.href = "/sign-in";
};

// Returns true when the token is missing, malformed, or past its expiry.
const isTokenInvalid = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    // A token with no `exp` claim is treated as invalid rather than eternal.
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  } catch {
    // Malformed/corrupt token: don't let it throw out of the link.
    return true;
  }
};

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  let token = "";
  if (typeof window !== "undefined") {
    token = localStorage.getItem("token") || "";
  }

  if (token && isTokenInvalid(token)) {
    if (typeof window !== "undefined") {
      handleTokenExpiry();
    }
    token = "";
  }

  operation.setContext(({ headers = {} }) => {
    return {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    };
  });

  return forward(operation);
});

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_SERVER_URL,
});
const errorLink = onError(({ operation, graphQLErrors }) => {
  const { response } = operation.getContext();
  if (response?.status === 500) {
    if (graphQLErrors?.length) {
      if (graphQLErrors[0]?.message === "Context creation failed: invalid signature") {
        if (typeof window !== "undefined") {
          handleTokenExpiry();
        }
      }
    }
  }
});
export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([authMiddleware, errorLink, httpLink]),
});
